import { Card, DatePicker } from "antd";
import Layout from '../../Layout'
import React, { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import ReportTables from "./ReportTables";
import dayjs, { Dayjs } from "dayjs";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
const { RangePicker } = DatePicker;

export default function Dashboard() {
    const { t } = useTranslation();
    // ================= PIE =================
    const pieOption = useMemo(() => ({
        tooltip: { trigger: "item" },
        legend: {
            orient: "vertical",
            right: 0,
            textStyle: {
                color: "var(--text-color)"
            }
        },
        series: [
            {
                type: "pie",
                radius: "70%",
                data: [
                    { value: 69.3, name: t('report.facebook') },
                    { value: 9.6, name: t('report.referral') },
                    { value: 5, name: t('report.not_recorded') },
                    { value: 4, name: t('report.walkin') },
                    { value: 3, name: t('report.hotline') },
                    { value: 3, name: t('report.old') },
                    { value: 2, name: t('report.ctv') },
                    { value: 2, name: t('report.kols') },
                    { value: 1, name: t('report.other') }
                ],
            }
        ]
    }), []);

    // ================= BAR 1 =================
    const barCustomerOption = useMemo(() => ({
        tooltip: { trigger: "axis" },
        legend: {
            data: [t('report.month_1'), t('report.month_2'), t('report.month_3')],
            textStyle: { color: "var(--text-color)" }
        },
        grid: {
            left: "0",
            right: "0",
            bottom: "0",
            containLabel: true
        },
        xAxis: {
            type: "category",
            data: [t('report.new_customer'), t('report.payment_customer'), t('report.old_customer'), t('report.return_customer')],
            axisLabel: { color: "var(--text-color)" }
        },
        yAxis: {
            type: "value",
            axisLabel: { color: "var(--text-color)" }
        },
        series: [
            {
                name: t('report.month_1'),
                type: "bar",
                data: [94, 4, 11, 4]
            },
            {
                name: t('report.month_2'),
                type: "bar",
                data: [69, 24, 4, 0]
            },
            {
                name: t('report.month_3'),
                type: "bar",
                data: [41, 13, 2, 0]
            }
        ]
    }), []);

    // ================= BAR 2 =================
    const barServiceOption = useMemo(() => ({
        tooltip: {
            trigger: "axis",
            formatter: (params: any) => {
                const val = params[0].value;
                return `${params[0].name}<br/>${val} ${t('report.million_short', 'Tr')}`;
            }
        },
        grid: {
            left: "0",
            right: "0",
            bottom: "0",
            containLabel: true
        },
        xAxis: {
            type: "category",
            data: ["New Hair", "Branding", "Google", "Tiktok", "CNC", "Căng chỉ", "TMV", "DV khác"],
            axisLabel: { color: "var(--text-color)" }
        },
        yAxis: {
            type: "value",
            axisLabel: {
                color: "var(--text-color)",
                formatter: (value: number) => `${value} Tr`
            }
        },
        series: [
            {
                name: t('report.month_3'),
                type: "bar",
                data: [264, 29, 2, 25, 130, 12, 74, 17],
                label: {
                    show: true,
                    position: "top",
                    formatter: ({ value }: any) => `${value} Tr`
                }
            }
        ]
    }), []);

    // ================= LINE =================
    const lineOption = useMemo(() => ({
        tooltip: {
            trigger: "axis",
            formatter: (params: any) => {
                let html = `<strong>Ngày ${params[0].axisValue}</strong><br/>`;
                params.forEach((item: any) => {
                    html += `${item.marker} ${item.seriesName}: <b>${Math.round(item.value)} Tr</b><br/>`;
                });
                return html;
            }
        },

        legend: {
            formatter: (name: string) => {
                return name; // 👈 hiển thị text sau icon
            },

            data: [t('report.month_1'), t('report.month_2'), t('report.month_3')]
        },

        grid: {
            left: "0%",
            right: "0%",
            bottom: "0%",
            containLabel: true
        },

        xAxis: {
            type: "category",
            data: Array.from({ length: 31 }, (_, i) => i + 1),
            axisLabel: { color: "var(--text-color)" }
        },

        yAxis: {
            type: "value",
            axisLabel: {
                color: "var(--text-color)",
                formatter: (v: number) => `${v} Tr`
            }
        },

        series: [
            {
                name: t('report.month_1'),
                type: "line",
                smooth: true,
                data: Array.from({ length: 31 }, () => Math.random() * 50 + 20),

                label: { show: false },

                emphasis: {
                    label: {
                        show: true,
                        formatter: (p: any) => `${Math.round(p.value)} Tr`
                    }
                },

                endLabel: {
                    show: true,
                    formatter: "T1",
                    color: "#3b82f6",
                    fontWeight: 600
                }
            },

            {
                name: t('report.month_2'),
                type: "line",
                smooth: true,
                data: Array.from({ length: 31 }, () => Math.random() * 40 + 20),

                label: { show: false },

                emphasis: {
                    label: {
                        show: true,
                        formatter: (p: any) => `${Math.round(p.value)} Tr`
                    }
                },

                endLabel: {
                    show: true,
                    formatter: "T2",
                    color: "#22c55e",
                    fontWeight: 600
                }
            },

            {
                name: t('report.month_3'),
                type: "line",
                smooth: true,
                data: Array.from({ length: 31 }, (_, i) =>
                    i > 11 ? 0 : Math.random() * 50 + 20
                ),

                label: { show: false },

                emphasis: {
                    label: {
                        show: true,
                        formatter: (p: any) => `${Math.round(p.value)} Tr`
                    }
                },

                endLabel: {
                    show: true,
                    formatter: "T3",
                    color: "#f59e0b",
                    fontWeight: 600
                }
            }
        ]
    }), []);

    const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().startOf("month"),
        dayjs().endOf("month"),
    ]);

    return (
        <Layout>
            <Helmet>
                <title>{t('report.title')} | Khothi</title>
            </Helmet>

            {/* TOP */}
            <div>
                <h2 className="text-xl font-bold text-gray-900 text-000000e0 mb-4">
                    {t('report.title')}
                </h2>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 xl:gap-4 gap-y-4 mb-4">

                {/* LEFT */}
                <Card className="col-span-1 dark:bg-[#1e293b]">
                    <div className="space-y-4">

                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                ({t('report.select_range')})
                            </p>

                            <RangePicker
                                value={range}
                                onChange={(val) => setRange(val as [Dayjs, Dayjs])}
                                className="w-full"
                            />
                        </div>

                        <div>
                            <h2 className="font-semibold mb-2 text-000000e0 text-[16px]">
                                {t('report.customer_source')}
                            </h2>

                            <ReactECharts option={pieOption} style={{ height: 300 }} />
                        </div>

                    </div>
                </Card>

                {/* RIGHT */}
                <div className="col-span-2 space-y-4">

                    <Card className="dark:bg-[#1e293b]">
                        <h2 className="font-semibold mb-2 text-000000e0 text-[16px]">
                            {t('report.customer_status_by_month')}
                        </h2>
                        <ReactECharts option={barCustomerOption} style={{ height: 300 }} />
                    </Card>

                    <Card className="dark:bg-[#1e293b]">
                        <h2 className="font-semibold mb-2 text-000000e0 text-[16px]">
                            {t('report.service_cost_by_group')}
                        </h2>
                        <ReactECharts option={barServiceOption} style={{ height: 300 }} />
                    </Card>

                </div>
            </div>

            {/* BOTTOM */}
            <Card className="dark:bg-[#1e293b] mb-4">
                <h2 className="font-semibold mb-2 text-000000e0 text-[16px]">
                    {t('report.cost_by_day_month')}
                </h2>
                <ReactECharts option={lineOption} style={{ height: 350 }} />
            </Card>

            <ReportTables />
        </Layout>
    );
}